// src/components/LayerPanel/BasemapSelector.jsx
//
// Picker for the map's underlying raster basemap (Mapbox "classic" styles —
// Satellite Streets, Satellite, Streets, Outdoors, Light, Dark). Renders
// above PanelHeader so it's visible regardless of operating/user AOI state:
// it's a display preference, not scoped to either AOI concept.
//
// With no props it is Explore's picker. PadPlanner's maps pass their own list
// and selection (useBasemapStore PADPLANNER_BASEMAPS, padplannerBasemapId) and
// overlay: a compact box floating over the map.

import { useState, useRef, useEffect } from "react";
import { Map as MapIcon, ChevronDown, Check } from "lucide-react";
import { useBasemapStore, CLASSIC_BASEMAPS } from "../../stores/useBasemapStore";

function cx(...c) { return c.filter(Boolean).join(" "); }

export default function BasemapSelector({ options, value, onChange, overlay = false } = {}) {
  const storeId = useBasemapStore((s) => s.activeBasemapId);
  const storeSet = useBasemapStore((s) => s.setBasemap);
  const choices = options ?? CLASSIC_BASEMAPS;
  const activeBasemapId = value ?? storeId;
  const setBasemap = onChange ?? storeSet;
  const active =
    choices.find((b) => b.id === activeBasemapId) ?? choices[0];

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className={overlay
        ? "relative w-40 rounded-md border border-slate-700 bg-black/60 px-2.5 py-1.5 backdrop-blur-sm"
        : "flex-shrink-0 relative px-2.5 py-2 border-b border-slate-800 bg-bg-softer"}
      data-testid="basemap-selector"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Change basemap"
        className="flex items-center gap-2 w-full min-w-0 text-left text-[11px] text-gray-300 hover:text-gray-100 transition"
      >
        <MapIcon size={11} className="text-fg-secondary flex-shrink-0" />
        <span className="flex-1 min-w-0 truncate">{active.label}</span>
        <ChevronDown size={11} className="text-fg-secondary flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto rounded-md border border-slate-700 bg-bg-softer shadow-xl shadow-black/50 py-1">
          {choices.map((b) => {
            const isActive = b.id === activeBasemapId;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => { setBasemap(b.id); setOpen(false); }}
                className={cx(
                  "w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition",
                  isActive
                    ? "text-accent-action"
                    : "text-gray-300 hover:bg-slate-700 hover:text-white"
                )}
              >
                <MapIcon size={11} className="flex-shrink-0" />
                <span className="flex-1 truncate">{b.label}</span>
                {isActive && <Check size={11} className="flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
